// Partner Promotions Eligibility Checker
// This module handles checking user eligibility for partner promotions

import {  getSubscriptions } from "//bark.co/cdn/shop/t/282/assets/ordergroove.js?v=10848403420202116351788363986";
import { loggedIn } from "//bark.co/cdn/shop/t/282/assets/utils.js?v=48097076372142184661783451415";

function showDefaultState() {
  document.querySelectorAll('.partner-promotion-card').forEach(card => {
    const button = card.querySelector('.partner-promotion-card__accept-offer-button');
    const message = card.querySelector('.partner-promotion-card__message');

    button.classList.remove('partner-promotion-card__button--loading');
    button.style.display = 'none'; // Hide claim button by default

    if (message) {
      message.style.display = 'block'; // Show subscribe message for ineligible users
    }
  });
}

function determinePageState(isEligible, acceptedCount, totalOffers) {
  if (!loggedIn() || !isEligible) {
    return 'ineligible';
  }

  if (acceptedCount >= totalOffers) {
    return 'no offers available';
  }

  return 'offers available';
}

function trackPageLoad(pageState) {
  if (window.Shopify && window.Shopify.analytics) {
    window.Shopify.analytics.publish('rudderstack_track', {
      eventName: 'Partner Offers Page Loaded',
      eventData: {
        page_state: pageState
      }
    });
  }
}

function setupSubscriptionClickHandlers() {
  document.querySelectorAll('.partner-promotion-card__subscribe-link').forEach(link => {
    link.addEventListener('click', function (event) {
      const partnerName = this.getAttribute('data-partner-name');

      if (window.Shopify && window.Shopify.analytics) {
        window.Shopify.analytics.publish('rudderstack_track', {
          eventName: 'Partner Offers Sub Signup Clicked',
          eventData: {
            partner_name: partnerName
          }
        });
      }
    });
  });
}

async function checkEligibility() {
  // Get customer email from data attribute
  const container = document.querySelector('.partner-promotions__container');
  const { customerEmail } = container?.dataset || {};
  const isLoggedIn = loggedIn();

  let isEmployee = !!(customerEmail && customerEmail.endsWith('@barkbox.com'));
  // Count total available offers on the page
  const totalOffers = document.querySelectorAll('.partner-promotion-card').length;

  // If user is not logged in, show default state and return early
  if (!isLoggedIn) {
    showDefaultState();
    trackPageLoad('ineligible');
    setupSubscriptionClickHandlers();
    updatePromotionUI({ isEligible: false });
    return;
  }

  // Check if user has a @barkbox.com email - if so, they are an employee
  if (isEmployee) {
    // For employees, handle per-card eligibility based on employee_eligible field
    const acceptedPromotions = new Set(JSON.parse(localStorage.getItem('acceptedPromotions') || '[]'));
    let visibleOffersCount = 0;

    document.querySelectorAll('.partner-promotion-card').forEach(card => {
      const button = card.querySelector('.partner-promotion-card__accept-offer-button');
      const message = card.querySelector('.partner-promotion-card__message');
      const promotionKey = button?.getAttribute('data-unique-key');
      const employeeEligible = card.getAttribute('data-employee-eligible');

      // Hide cards that explicitly exclude employees
      if (employeeEligible === 'false') {
        card.style.display = 'none';
        return;
      }

      // Show card (employee_eligible is true, empty, or undefined)
      card.style.display = '';
      visibleOffersCount++;
      button.classList.remove('partner-promotion-card__button--loading');

      if (acceptedPromotions.has(promotionKey)) {
        button.style.display = 'none';
        if (message) message.style.display = 'none';
      } else {
        button.style.display = 'block';
        if (message) message.style.display = 'none';
      }
    });

    // Determine page state for tracking
    const pageState = determinePageState(true, acceptedPromotions.size, visibleOffersCount);
    trackPageLoad(pageState);
    setupSubscriptionClickHandlers();
    updatePromotionUI({ isEligible: true });
    return;
  }

  try {
    const ogSigDataDiv = document.querySelector(".og-sig-data");
    if (!ogSigDataDiv) {
      showDefaultState();
      trackPageLoad('ineligible');
      setupSubscriptionClickHandlers();
      updatePromotionUI({ isEligible: false });
      return;
    }
    const subscriptions = await getSubscriptions();

    // Check if any subscription is an active 6 or 12 month commitment
    const isEligible = subscriptions.some(sub => {
      // Check if subscription has commitment data
      if (!sub.extra_data?.commitment?.orders_per_commitment) {
        return false;
      }

      // Check if subscription state is active or disable_autorenewal
      const validStates = ['active', 'disable_autorenewal'];
      const isActive = validStates.includes(sub.extra_data.commitment.state);

      // Check if commitment is 6 months or more
      const ordersPerCommitment = parseInt(sub.extra_data.commitment.orders_per_commitment);
      const isLongTerm = ordersPerCommitment >= 6;

      return isActive && isLongTerm;
    });

    // Check if user has accepted any offers
    const acceptedPromotions = new Set(JSON.parse(localStorage.getItem('acceptedPromotions') || '[]'));
    const acceptedCount = acceptedPromotions.size;

    // Determine page state and track
    const pageState = determinePageState(isEligible, acceptedCount, totalOffers);
    trackPageLoad(pageState);

    // Update all promotion cards
    document.querySelectorAll('.partner-promotion-card').forEach(card => {
      const button = card.querySelector('.partner-promotion-card__accept-offer-button');
      // find subscribe button just in case someone has claimed offer but is no longer eligible
      const subscribeButton = card.querySelector('.partner-promotion-card__subscribe-link');
      const message = card.querySelector('.partner-promotion-card__message');
      const promotionKey = button?.getAttribute('data-unique-key');
      button.classList.remove('partner-promotion-card__button--loading');

      if (isEligible && isLoggedIn && !acceptedPromotions.has(promotionKey)) {
        button.style.display = 'block'; // Show claim button for eligible users
        if (message) message.style.display = 'none'; // Hide subscribe message
      } else if (isLoggedIn && acceptedPromotions.has(promotionKey)) {
        button.style.display = 'none'; // Hide claim button for already accepted users
        subscribeButton.style.display = 'none'; // Hide subscribe button for already accepted users
        if (message) message.style.display = 'none'; // Hide subscribe message
      } else {
        button.style.display = 'none'; // Hide claim button for ineligible users
        if (message) message.style.display = 'block'; // Show subscribe message
      }
    });

    // Setup click handlers after DOM is updated
    setupSubscriptionClickHandlers();

    // Update promotion UI
    updatePromotionUI({ isEligible });
  } catch (error) {
    showDefaultState();
    trackPageLoad('ineligible');
    setupSubscriptionClickHandlers();
    updatePromotionUI({ isEligible: false, });
  }
}

function updatePromotionUI({ isEligible }) {
  const isLoggedIn = loggedIn();
  // Committee row
  const committeeRow = document.querySelector('.bark-committee-row');
  if (committeeRow) {
    committeeRow.style.display = (!isEligible || !isLoggedIn) ? 'flex' : 'none';
  }

  // Subtitles - Desktop
  const subscriberSubtitle = document.getElementById('bark-subscriber-subtitle');
  const prospectSubtitle = document.getElementById('bark-prospect-subtitle');
  if (subscriberSubtitle) {
    subscriberSubtitle.style.display = isEligible ? '' : 'none';
  }
  if (prospectSubtitle) {
    prospectSubtitle.style.display = !isEligible ? '' : 'none';
  }

  // Subtitles - Mobile
  const subscriberSubtitleMobile = document.getElementById('bark-subscriber-subtitle-mobile');
  const prospectSubtitleMobile = document.getElementById('bark-prospect-subtitle-mobile');
  if (subscriberSubtitleMobile) {
    subscriberSubtitleMobile.style.display = isEligible ? '' : 'none';
  }
  if (prospectSubtitleMobile) {
    prospectSubtitleMobile.style.display = !isEligible ? '' : 'none';
  }

  // Offers require snippet
  const offersRequireSnippet = document.getElementById('bark-promo-eligibility-note');
  if (offersRequireSnippet) {
    offersRequireSnippet.style.display = !isEligible ? '' : 'none';
  }

  // Disclaimer messages
  const eligibleSubscriberDisclaimer = document.querySelectorAll('.bark-eligible-subscriber-disclaimer');
  const prospectDisclaimer = document.querySelectorAll('.bark-prospect-disclaimer');
  eligibleSubscriberDisclaimer.forEach((el) => {
    el.style.display = (isLoggedIn && isEligible) ? 'block' : 'none';
  });
  prospectDisclaimer.forEach((el) => {
    el.style.display = (!isLoggedIn || !isEligible) ? 'block' : 'none';
  });
}

if (document.readyState !== 'loading') {
  checkEligibility();
} else {
  document.addEventListener('DOMContentLoaded', checkEligibility);
}
